'use client';

import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { m, useInView, useReducedMotion } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { ProcessVisual } from './content/types';

const EASE = [0.16, 1, 0.3, 1] as const;

function Sheet({
  title,
  meta,
  children,
  label,
}: {
  title: string;
  meta: string;
  children: ReactNode;
  label: string;
}) {
  return (
    <figure
      aria-label={label}
      className="w-full rounded-[1.25rem] bg-white text-ink-900 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]"
    >
      <div className="rule-ink flex items-baseline justify-between gap-4 border-b px-5 py-3.5">
        <p className="font-display text-[1.05rem] font-bold tracking-[-0.01em]">{title}</p>
        <p className="tabular shrink-0 text-[0.8rem] text-muted">{meta}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </figure>
  );
}

/** One reveal per sheet, fired when the sheet is in view. Reduced motion shows the end state. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-25% 0px' });
  const reduce = useReducedMotion();
  const [forced, setForced] = useState(false);
  useEffect(() => {
    if (reduce) setForced(true);
  }, [reduce]);
  return { ref, show: inView || forced, reduce: !!reduce };
}

function Check({ state }: { state: 'met' | 'missing' | 'pending' }) {
  return (
    <span
      className={clsx(
        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
        state === 'met' && 'border-ink-900 bg-ink-900 text-white',
        state === 'missing' && 'border-rose bg-rose text-white',
        state === 'pending' && 'border-ink-900/25 bg-white',
      )}
      aria-hidden="true"
    >
      {state === 'met' ? <CheckIcon className="h-3.5 w-3.5" /> : null}
      {state === 'missing' ? <span className="block h-1.5 w-1.5 rounded-full bg-white" /> : null}
    </span>
  );
}

const RULES = [
  { text: 'Epworth Sleepiness Scale documented', note: 'ESS 14, recorded 12 May', missing: false },
  { text: 'Symptoms present for at least three months', note: 'Snoring, witnessed apneas', missing: false },
  { text: 'BMI and neck circumference recorded', note: 'BMI 34, neck 17.5 in', missing: false },
  { text: 'Home sleep test failed or contraindicated', note: 'Add HST result or physician note', missing: true },
  { text: 'Order with diagnosis code', note: 'G47.33, signed', missing: false },
];

function RulesSheet({ payer }: { payer: string }) {
  const { ref, show } = useReveal();
  return (
    <div ref={ref}>
      <Sheet title={`${payer} requirements`} meta="95810" label="A payer requirement checklist with one open item">
        <ul className="divide-y divide-ink-900/10">
          {RULES.map((r) => (
            <li key={r.text} className="flex items-start gap-3 py-2.5">
              <Check state={r.missing ? (show ? 'missing' : 'pending') : 'met'} />
              <div className="min-w-0">
                <p className="text-[0.9rem] leading-snug font-medium">{r.text}</p>
                <p
                  className={clsx(
                    'text-[0.8rem] leading-snug transition-colors duration-300',
                    r.missing && show ? 'text-rose' : 'text-muted',
                  )}
                >
                  {r.note}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <p
          className={clsx(
            'rule-ink mt-3 border-t pt-3 text-[0.85rem] font-semibold transition-colors duration-300',
            show ? 'text-rose' : 'text-muted',
          )}
        >
          {show ? '1 requirement open. Do not submit yet.' : 'Checking the chart against the list.'}
        </p>
      </Sheet>
    </div>
  );
}

const LETTER_LINES: { text: string; marks?: string[] }[] = [
  {
    text: 'The patient presents with excessive daytime sleepiness (Epworth 14), loud snoring and witnessed apneas reported by a bed partner, persisting for more than six months.',
    marks: ['excessive daytime sleepiness (Epworth 14)', 'witnessed apneas'],
  },
  {
    text: 'Examination shows a BMI of 34 and a neck circumference of 17.5 inches. The patient has hypertension that remains poorly controlled on two agents.',
    marks: ['BMI of 34', 'hypertension'],
  },
  {
    text: 'An attended polysomnography (CPT 95810) is medically necessary to confirm obstructive sleep apnea and to guide therapy. A home study is contraindicated given the cardiovascular comorbidity.',
    marks: ['CPT 95810', 'contraindicated'],
  },
];

function Marked({ text, marks, show, delay }: { text: string; marks?: string[]; show: boolean; delay: number }) {
  if (!marks?.length) return <>{text}</>;
  const pattern = new RegExp(`(${marks.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`);
  const parts = text.split(pattern);
  let k = 0;
  return (
    <>
      {parts.map((part, i) =>
        marks.includes(part) ? (
          <span key={i} className="relative inline">
            <m.span
              aria-hidden="true"
              initial={{ scaleX: 0 }}
              animate={show ? { scaleX: 1 } : undefined}
              transition={{ duration: 0.5, ease: EASE, delay: delay + 0.15 * k++ }}
              className="absolute inset-x-[-2px] inset-y-[-1px] -z-[1] origin-left rounded-[2px] bg-beacon-100"
            />
            <span className="relative">{part}</span>
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function LetterSheet({ payer }: { payer: string }) {
  const { ref, show, reduce } = useReveal();
  return (
    <div ref={ref}>
      <Sheet title="Letter of medical necessity" meta={`To ${payer}`} label="A drafted letter of medical necessity with key clinical findings highlighted">
        <div className="text-[0.95rem] leading-[1.55] text-ink-900 sm:text-base">
          <p className="mb-3 text-muted">
            Re: Attended polysomnography, CPT 95810. Diagnosis G47.33, obstructive sleep apnea.
          </p>
          {LETTER_LINES.map((line, i) => (
            <p key={i} className="relative mb-3 isolate">
              <Marked text={line.text} marks={line.marks} show={show && !reduce} delay={0.2 + i * 0.3} />
            </p>
          ))}
          <p className="rule-ink mt-5 border-t pt-3 text-[0.8rem] text-muted">
            Electronically signed by the ordering physician. Ready for submission.
          </p>
        </div>
      </Sheet>
    </div>
  );
}

const FIELDS = [
  { label: 'Member ID', value: 'W281 440 912' },
  { label: 'Service requested', value: 'Attended polysomnography' },
  { label: 'CPT', value: '95810' },
  { label: 'ICD-10', value: 'G47.33' },
  { label: 'Place of service', value: '22, outpatient hospital' },
  { label: 'Rendering provider NPI', value: '1538 221 907' },
  { label: 'Supporting documents', value: 'Letter, ESS, HST result' },
];

function ReadySheet({ payer }: { payer: string }) {
  const { ref, show } = useReveal();
  return (
    <div ref={ref}>
      <Sheet title={`${payer} authorization request`} meta="Form 7 of 7" label="A payer authorization form with every field completed">
        <dl className="divide-y divide-ink-900/10">
          {FIELDS.map((f) => (
            <div key={f.label} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-3 py-2">
              <dt className="text-[0.8rem] text-muted">{f.label}</dt>
              <dd className="tabular text-[0.9rem] font-medium">{f.value}</dd>
            </div>
          ))}
        </dl>
        <div
          className={clsx(
            'mt-4 flex items-center justify-between gap-4 rounded-md px-4 py-3 transition-colors duration-500',
            show ? 'bg-verdant-soft' : 'bg-frost-100',
          )}
        >
          <p className={clsx('text-[0.85rem] font-semibold transition-colors duration-500', show ? 'text-verdant' : 'text-muted')}>
            {show ? '0 requirements open. Ready to submit.' : 'Checking every field against the list.'}
          </p>
          <span className="rounded-full bg-beacon-500 px-3.5 py-1.5 text-[0.8rem] font-semibold text-white">Submit</span>
        </div>
      </Sheet>
    </div>
  );
}

const CASES = [
  { id: '0142', patient: 'A. W.', study: 'PSG 95810', payer: 'Aetna', age: '1 d', status: 'Approved', tone: 'verdant' },
  { id: '0141', patient: 'B. K.', study: 'HST 95806', payer: 'Cigna', age: '3 d', status: 'Pending review', tone: 'ink' },
  { id: '0139', patient: 'C. R.', study: 'CPAP E0601', payer: 'Medicaid', age: '2 d', status: 'Needs HST result', tone: 'muted' },
  { id: '0138', patient: 'D. M.', study: 'PSG 95811', payer: 'UHC', age: '6 h', status: 'Submitted', tone: 'ink' },
  { id: '0135', patient: 'E. L.', study: 'BiPAP E0470', payer: 'Humana', age: '9 d', status: 'Denied, appeal drafted', tone: 'rose' },
] as const;

function BoardSheet() {
  return (
    <Sheet title="Open cases" meta="5 of 23" label="A board of prior authorization cases with their status and age">
      <table className="w-full border-collapse text-left text-[0.85rem]">
        <thead>
          <tr className="rule-ink border-b text-[0.75rem] text-muted">
            <th scope="col" className="py-2 pr-3 font-medium">Case</th>
            <th scope="col" className="py-2 pr-3 font-medium">Study</th>
            <th scope="col" className="hidden py-2 pr-3 font-medium sm:table-cell">Plan</th>
            <th scope="col" className="py-2 pr-3 font-medium">Age</th>
            <th scope="col" className="py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {CASES.map((c) => (
            <tr key={c.id} className="rule-ink border-b last:border-b-0">
              <td className="py-2.5 pr-3 align-top">
                <span className="tabular block font-medium">{c.id}</span>
                <span className="block text-[0.75rem] text-muted">{c.patient}</span>
              </td>
              <td className="tabular py-2.5 pr-3 align-top">{c.study}</td>
              <td className="hidden py-2.5 pr-3 align-top sm:table-cell">{c.payer}</td>
              <td className="tabular py-2.5 pr-3 align-top text-muted">{c.age}</td>
              <td className="py-2.5 align-top">
                <span
                  className={clsx(
                    'inline-flex items-center gap-1.5 font-medium',
                    c.tone === 'verdant' && 'text-verdant',
                    c.tone === 'rose' && 'text-rose',
                    c.tone === 'muted' && 'text-muted',
                    c.tone === 'ink' && 'text-ink-900',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={clsx(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      c.tone === 'verdant' && 'bg-verdant',
                      c.tone === 'rose' && 'bg-rose',
                      c.tone === 'muted' && 'bg-muted',
                      c.tone === 'ink' && 'bg-ink-900/50',
                    )}
                  />
                  {c.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Sheet>
  );
}

export function ProcessVisualFor({ visual, payer }: { visual: ProcessVisual; payer: string }) {
  switch (visual) {
    case 'rules':
      return <RulesSheet payer={payer} />;
    case 'letter':
      return <LetterSheet payer={payer} />;
    case 'ready':
      return <ReadySheet payer={payer} />;
    case 'board':
      return <BoardSheet />;
  }
}
